<template>
  <div class="account-page">
    <AccountPageTopBar :title="$t('accountPage.title')" :show-back="false" />

    <main class="account-page__content">
      <button class="account-user-card" type="button" :disabled="isLogin" @click="handleUserClick">
        <img class="account-user-card__avatar" src="@/assets/img/home/genggo-head.png" :alt="$t('accountPage.avatar')">
        <div class="account-user-card__body">
          <strong>{{ isLogin ? displayPhone : $t('accountPage.guestUser') }}</strong>

          <template v-if="isLogin">
            <div class="account-user-card__meta" :class="{ 'is-verified': memberVerified }">
              <img :src="memberVerified ? verifiedIcon : unverifiedIcon" alt="">
              <span>{{ memberVerified ? $t('accountPage.verifiedMember') : $t('accountPage.unverifiedMember') }}</span>
            </div>
            <div class="account-user-card__meta account-user-card__meta--muted">
              <img src="@/assets/img/profile/account/calendar.svg" alt="">
              <span>{{ memberSinceText }}</span>
            </div>
          </template>

          <template v-else>
            <p>{{ $t('accountPage.loginDescription') }}</p>
            <span class="account-user-card__login">
              {{ $t('login.login_button') }}
              <Icon name="arrow" />
            </span>
          </template>
        </div>
      </button>

      <section class="account-shortcuts" :aria-label="$t('accountPage.shortcutsAriaLabel')">
        <button type="button" @click="openOrders">
          <span class="account-shortcuts__icon account-shortcuts__icon--orders">
            <img src="@/assets/img/profile/account/orders.svg" alt="">
          </span>
          <span>{{ $t('accountPage.myOrders') }}</span>
        </button>
        <button type="button" @click="openProtected('/rentalplan/list')">
          <span class="account-shortcuts__icon account-shortcuts__icon--bills">
            <img src="@/assets/img/profile/account/bills.svg" alt="">
          </span>
          <span>{{ $t('accountPage.myBills') }}</span>
        </button>
      </section>

      <section class="account-menu-card">
        <button
          v-for="(item, index) in menuItems"
          :key="item.key"
          type="button"
          :class="{ 'has-divider': index > 0 }"
          @click="handleMenuClick(item.key)"
        >
          <span class="account-menu-card__icon"><img :src="item.icon" alt=""></span>
          <span class="account-menu-card__copy">
            <strong>{{ $t(item.titleKey) }}</strong>
            <small>{{ item.subtitle }}</small>
          </span>
          <Icon class="account-menu-card__arrow" name="arrow" />
        </button>
      </section>

      <button class="account-support-card" type="button" @click="openCustomerSupport">
        <span class="account-support-card__icon"><img src="@/assets/img/profile/account/whatsapp.svg" alt=""></span>
        <span class="account-support-card__copy">
          <strong>{{ $t('customerService') }}</strong>
          <small>{{ $t('accountPage.supportDescription') }}</small>
        </span>
        <Icon class="account-support-card__arrow" name="arrow" />
      </button>

      <button v-if="isLogin" class="account-logout" type="button" @click="logout">
        <img src="@/assets/img/profile/account/logout.svg" alt="">
        <span>{{ $t('logout') }}</span>
      </button>
    </main>

    <LanguageSwitcher ref="languageSwitcher" />
    <AppBottomNav />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { Dialog, Icon } from 'vant';
import { getMemberInfo } from '@/api/member';
import { getAppSystemConfig } from '@/api/common';
import { LANGUAGE } from '@/common/constants';
import { buildCustomerSupportWhatsAppUrl } from '@/common/customerSupport';
import { DEFAULT_REG_CHANNEL } from '@/config';
import AccountPageTopBar from '@/components/AccountPageTopBar/index.vue';
import AppBottomNav from '@/components/AppBottomNav/index.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';

export default {
  name: 'Profile',
  components: {
    AccountPageTopBar,
    AppBottomNav,
    Icon,
    LanguageSwitcher,
  },
  data() {
    return {
      memberInfo: null,
      autoDebitEnabled: false,
      verifiedIcon: require('@/assets/img/profile/account/verified.svg'),
      unverifiedIcon: require('@/assets/img/profile/account/unverified.svg'),
    };
  },
  computed: {
    ...mapState('user', ['session', 'userInfo']),
    isLogin() {
      return !!this.session?.accessToken;
    },
    displayPhone() {
      const phone = this.memberInfo?.mobile || this.userInfo?.phoneNumber || '';
      return this.formatAccountPhone(phone);
    },
    memberVerified() {
      const kyc = String(this.memberInfo?.kyc || '').toUpperCase();
      const ocr = String(this.memberInfo?.ocr || '').toUpperCase();
      return kyc === 'SUCCESS' && ocr === 'SUCCESS';
    },
    memberSinceText() {
      const date = this.formatMemberDate(this.memberInfo?.regTime);
      return date
        ? this.$t('accountPage.memberSince', { date })
        : this.$t('accountPage.memberSinceEmpty');
    },
    languageLabel() {
      if (this.$i18n.locale === LANGUAGE.id) return 'Bahasa Indonesia';
      return 'English';
    },
    menuItems() {
      const items = [
        {
          key: 'information',
          icon: require('@/assets/img/profile/account/information.svg'),
          titleKey: 'accountPage.myInformation',
          subtitle: this.$t('accountPage.myInformationDescription'),
        },
        {
          key: 'address',
          icon: require('@/assets/img/profile/account/address.svg'),
          titleKey: 'accountPage.addressesContacts',
          subtitle: this.$t('accountPage.addressesContactsDescription'),
        },
        {
          key: 'paymentMethods',
          icon: require('@/assets/img/profile/account/payment-methods.svg'),
          titleKey: 'accountPage.paymentMethods',
          subtitle: this.$t('accountPage.paymentMethodsDescription'),
        },
        {
          key: 'faq',
          icon: require('@/assets/img/profile/account/faq.svg'),
          titleKey: 'FAQ',
          subtitle: this.$t('accountPage.faqDescription'),
        },
        {
          key: 'settings',
          icon: require('@/assets/img/profile/account/settings.svg'),
          titleKey: 'accountPage.settings',
          subtitle: this.$t('accountPage.settingsDescription'),
        },
        {
          key: 'language',
          icon: require('@/assets/img/profile/account/language.svg'),
          titleKey: 'language',
          subtitle: this.languageLabel,
        },
      ];
      return this.autoDebitEnabled ? items : items.filter(item => item.key !== 'paymentMethods');
    },
  },
  watch: {
    'session.accessToken'(accessToken) {
      if (accessToken) {
        this.loadMemberInfo();
      } else {
        this.memberInfo = null;
      }
    },
  },
  mounted() {
    this.loadMemberInfo();
    this.loadAutoDebitConfig();
    this.$nextTick(this.runPendingAction);
  },
  methods: {
    async loadAutoDebitConfig() {
      this.autoDebitEnabled = false;
      try {
        const res = await getAppSystemConfig({
          platform: 'h5',
          channel: this.$store.state.user.channel || DEFAULT_REG_CHANNEL
        });
        if (res?.code !== 'Success') return;
        const features = res.data?.features || res.data || {};
        this.autoDebitEnabled = [true, 'true', 1, '1'].includes(features.autoDebit);
      } catch (e) {
        // 配置读取失败时保持关闭，避免错误展示自动扣款入口。
      }
    },
    async loadMemberInfo() {
      if (!this.isLogin) {
        this.memberInfo = null;
        return;
      }

      try {
        const res = await getMemberInfo({ _showLoading: false });
        if (res?.code !== 'Success') throw new Error(res?.message || '');
        this.memberInfo = res.data || {};
      } catch (error) {
        this.memberInfo = null;
        this.$toast(error?.message || this.$t('accountPage.loadFailed'));
      }
    },
    formatAccountPhone(value) {
      const digits = String(value || '').replace(/\D/g, '');
      if (!digits) return '';
      let normalized = digits;
      if (normalized.startsWith('0')) normalized = `62${normalized.slice(1)}`;
      if (!normalized.startsWith('62')) normalized = `62${normalized}`;
      const local = normalized.slice(2);
      const groups = [];
      if (local) {
        groups.push(local.slice(0, 3));
        for (let index = 3; index < local.length; index += 4) {
          groups.push(local.slice(index, index + 4));
        }
      }
      return groups.length ? `+62 ${groups.join(' ')}` : '+62';
    },
    formatMemberDate(value) {
      const rawValue = String(value || '').trim();
      if (!rawValue) return '';
      let parsedValue = rawValue;
      if (/^\d+$/.test(rawValue)) {
        const numericValue = Number(rawValue);
        parsedValue = numericValue < 100000000000 ? numericValue * 1000 : numericValue;
      } else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(rawValue)) {
        parsedValue = rawValue.replace(' ', 'T');
      }
      const date = new Date(parsedValue);
      if (Number.isNaN(date.getTime())) return rawValue;
      const locale = this.$i18n.locale === LANGUAGE.id ? 'id-ID' : 'en-US';
      try {
        return new Intl.DateTimeFormat(locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }).format(date);
      } catch {
        return rawValue.slice(0, 10);
      }
    },
    handleUserClick() {
      if (!this.isLogin) this.goLogin('/profile/index');
    },
    goLogin(redirect) {
      this.$router.push({
        path: '/login',
        query: { redirect },
      });
    },
    confirmLoginRequired(redirect) {
      Dialog.confirm({
        title: this.$t('myPage.loginRequiredTitle'),
        message: this.$t('myPage.loginRequiredContent'),
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel'),
      })
        .then(() => this.goLogin(redirect))
        .catch(() => {});
    },
    openProtected(path) {
      if (this.isLogin) {
        this.$router.push(path);
        return;
      }
      this.confirmLoginRequired(path);
    },
    openOrders() {
      const route = {
        name: 'OrderList',
        query: {
          from: 'account',
          refreshToken: String(Date.now()),
        },
      };
      if (this.isLogin) {
        this.$router.push(route);
        return;
      }
      this.confirmLoginRequired('/order/list?from=account&refreshToken=' + route.query.refreshToken);
    },
    handleMenuClick(key) {
      if (key === 'information') {
        this.openProtected('/personal-additional-info');
      } else if (key === 'address') {
        this.openProtected('/address-contacts');
      } else if (key === 'paymentMethods') {
        this.openProtected('/payment/methods?backTo=%2Fprofile%2Findex');
      } else if (key === 'faq') {
        this.$router.push('/faq');
      } else if (key === 'settings') {
        this.$router.push('/profile/settings');
      } else if (key === 'language') {
        this.$refs.languageSwitcher.open();
      }
    },
    openAddress() {
      if (!this.isLogin) {
        this.confirmLoginRequired('/address-contacts');
        return;
      }
      this.$router.push('/address-contacts');
    },
    runPendingAction() {
      if (this.$route.query.action !== 'address' || !this.isLogin) return;
      this.$router.replace('/address-contacts');
    },
    openCustomerSupport() {
      window.open(buildCustomerSupportWhatsAppUrl(this.$store.state.user.channel), '_blank', 'noopener,noreferrer');
    },
    logout() {
      Dialog.confirm({
        title: this.$t('myPage.logoutTitle'),
        message: this.$t('myPage.logoutContent'),
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel'),
      })
        .then(async () => {
          const result = await this.$store.dispatch('user/logout');
          if (!result) return;
          this.memberInfo = null;
          this.$toast(this.$t('logout'));
          sessionStorage.setItem('isGoBack', 'true');
          this.$router.replace('/index');
        })
        .catch(() => {});
    },
  },
};
</script>

<style lang="less" scoped>
.account-page {
  min-height: 100vh;
  background: #f4f8ff;
  color: #070d3f;
}

.account-page__content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 112px 16px calc(164px + env(safe-area-inset-bottom));
  box-sizing: border-box;
}

.account-user-card,
.account-shortcuts > button,
.account-menu-card,
.account-support-card {
  border: 2px solid #e5eaf3;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: none;
}

button {
  font-family: inherit;
}

.account-user-card {
  display: flex;
  width: 100%;
  min-height: 144px;
  align-items: center;
  padding: 16px;
  color: inherit;
  text-align: left;
  box-sizing: border-box;

  &:disabled {
    opacity: 1;
  }
}

.account-user-card__avatar {
  width: 96px;
  height: 96px;
  flex-shrink: 0;
  border-radius: 20px;
  object-fit: cover;
}

.account-user-card__body {
  min-width: 0;
  margin-left: 16px;
  flex: 1;

  > strong {
    display: block;
    overflow: hidden;
    font-size: 30px;
    font-weight: 600;
    line-height: 44px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > p {
    margin: 8px 0 0;
    color: #6b7280;
    font-size: 24px;
    font-weight: 400;
    line-height: 32px;
  }
}

.account-user-card__meta {
  display: flex;
  min-width: 0;
  margin-top: 8px;
  align-items: center;
  gap: 8px;
  color: #ff7a00;
  font-size: 22px;
  font-weight: 500;
  line-height: 28px;

  &.is-verified {
    color: #0b45ff;
  }

  img {
    width: 30px;
    height: 30px;
    flex-shrink: 0;
  }

  &--muted {
    margin-top: 4px;
    color: #6b7280;
    font-weight: 400;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.account-user-card__login {
  display: inline-flex;
  margin-top: 16px;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border-radius: 20px;
  background: #eaf3ff;
  color: #0b45ff;
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
}

.account-shortcuts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  > button {
    display: flex;
    min-width: 0;
    height: 176px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding: 16px;
    color: #070d3f;
    box-sizing: border-box;

    > span:last-child {
      max-width: 100%;
      margin-top: 8px;
      overflow: hidden;
      font-size: 26px;
      font-weight: 500;
      line-height: 36px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.account-shortcuts__icon {
  display: flex;
  width: 76px;
  height: 76px;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: rgba(11, 69, 255, 0.1);
  color: #0b45ff;

  img {
    width: 40px;
    height: 40px;
  }

  &--bills {
    background: rgba(18, 201, 120, 0.1);
    color: #12c978;
  }
}

.account-menu-card {
  overflow: hidden;
  padding: 0 16px;
}

.account-menu-card > button {
  position: relative;
  display: flex;
  width: 100%;
  min-height: 112px;
  align-items: center;
  padding: 16px;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  box-sizing: border-box;

  &.has-divider::before {
    position: absolute;
    top: 0;
    right: 16px;
    left: 16px;
    height: 2px;
    background: #e5eaf3;
    content: '';
  }
}

.account-menu-card__icon {
  display: flex;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: #eaf3ff;
  color: #0b45ff;

  img {
    width: 30px;
    height: 30px;
  }
}

.account-menu-card__copy,
.account-support-card__copy {
  min-width: 0;
  margin-left: 16px;
  flex: 1;

  strong,
  small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-size: 26px;
    font-weight: 500;
    line-height: 36px;
  }

  small {
    margin-top: 8px;
    color: #6b7280;
    font-size: 24px;
    font-weight: 400;
    line-height: 32px;
  }
}

.account-menu-card__arrow,
.account-support-card__arrow {
  margin-left: 12px;
  flex-shrink: 0;
  color: #6b7280;
  font-size: 32px;
}

.account-support-card {
  display: flex;
  width: 100%;
  min-height: 120px;
  align-items: center;
  padding: 28px;
  border-color: #e5eaf3;
  background: #fff7ed;
  color: #ff7a00;
  text-align: left;
  box-sizing: border-box;

  .account-support-card__copy small {
    color: #6b7280;
  }
}

.account-support-card__icon {
  display: flex;
  width: 88px;
  height: 88px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: #ffffff;
  color: #25d366;

  img {
    width: 52px;
    height: 52px;
  }
}

.account-logout {
  display: flex;
  width: 100%;
  height: 92px;
  margin-top: 8px;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: 2px solid #ff2d16;
  border-radius: 20px;
  background: #fff1f1;
  color: #ff2d16;
  font-size: 30px;
  font-weight: 500;

  img {
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
  }

  &::after {
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    content: '';
  }
}
</style>
